import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

/**
 * Firebase target selection (three environments):
 * - dev  → triglavsindikattest (local `npm start`)
 * - qa   → sindikatqa (`npm run build:qa`)
 * - prod → production project (`npm run build:production`)
 */
function resolveFirebaseTarget() {
  const explicit = process.env.REACT_APP_FIREBASE_TARGET;
  if (explicit) {
    return String(explicit).trim().toLowerCase();
  }
  return process.env.NODE_ENV === 'development' ? 'dev' : 'prod';
}

const firebaseTarget = resolveFirebaseTarget();

const TARGET_SUFFIX = {
  dev: 'DEV',
  qa: 'QA',
  prod: 'PROD',
};

const suffix = TARGET_SUFFIX[firebaseTarget];
if (!suffix) {
  throw new Error(
    `[Firebase] Unknown REACT_APP_FIREBASE_TARGET="${firebaseTarget}". Expected dev, qa, or prod.`,
  );
}

function readFirebaseEnv(name) {
  return process.env[`REACT_APP_FIREBASE_${name}_${suffix}`];
}

const firebaseConfig = {
  apiKey: readFirebaseEnv('API_KEY'),
  authDomain: readFirebaseEnv('AUTH_DOMAIN'),
  projectId: readFirebaseEnv('PROJECT_ID'),
  storageBucket: readFirebaseEnv('STORAGE_BUCKET'),
  messagingSenderId: readFirebaseEnv('MESSAGING_SENDER_ID'),
  appId: readFirebaseEnv('APP_ID'),
};

const REQUIRED_KEYS = [
  'API_KEY',
  'AUTH_DOMAIN',
  'PROJECT_ID',
  'STORAGE_BUCKET',
  'MESSAGING_SENDER_ID',
  'APP_ID',
];

const missingKeys = REQUIRED_KEYS.filter((name) => !readFirebaseEnv(name));
if (missingKeys.length > 0) {
  const envNames = missingKeys.map((name) => `REACT_APP_FIREBASE_${name}_${suffix}`);
  throw new Error(
    `[Firebase] Missing ${firebaseTarget.toUpperCase()} env vars (${envNames.join(', ')}). ` +
      `See apps/web/FIREBASE_ENVIRONMENTS.md`,
  );
}

const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export default app;
export const firebaseProjectId = firebaseConfig.projectId;
export const firebaseTargetId = firebaseTarget;
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
